/**
 * What one AI player has seen (AI-02). Built only from public events and the player's own cards.
 *
 * Per hand (cleared on every fresh deal):
 *   - `held[seat]`: cards known or likely to be in an opponent's hand, with a probability. Taking a
 *     card from the face-up middle makes it certain (p = 1). A blind draw throws away one unseen card,
 *     so each tracked card of that player drops to p × 4/5 — we never claim to know which went.
 *   - `ownDiscards`: cards this AI threw away blind; they sit in the discard pile until it is
 *     shuffled into new bones, then they are back in circulation (unknown position).
 *   - `log`: every observation that set a card's whereabouts (for tests and diagnostics).
 * Per match (kept between hands): how often each opponent knocks and how strong their knocking
 * hands were, smoothed towards a neutral prior so one early knock does not label a player.
 */
import type { Card } from "../cards.ts";
import type { HandRecord, PublicEvent } from "../whiskey.ts";

export const cardKey = (c: Card): number => (c.rank - 2) * 4 + "CDHS".indexOf(c.suit);

export interface Held { p: number; reshuffledSince: boolean }
export interface Observation { hand: number; card: number; where: string; how: string }

export class CardMemory {
  readonly me: number;
  handNumber = 0;
  held: Map<number, Held>[] = [new Map(), new Map(), new Map(), new Map(), new Map()];
  ownDiscards = new Set<number>();
  log: Observation[] = [];
  private lastDiscardCount = 0;
  /** Set by the controller just before it draws blind: the card it is throwing away. */
  pendingOwnDiscard: Card | null = null;
  // match-level tendencies
  readonly knocks = [0, 0, 0, 0, 0];
  readonly handsSeen = [0, 0, 0, 0, 0];
  readonly knockCategorySum = [0, 0, 0, 0, 0];
  readonly wins = [0, 0, 0, 0, 0];

  constructor(me: number) {
    this.me = me;
  }

  newHand(handNumber: number): void {
    this.handNumber = handNumber;
    this.held = [new Map(), new Map(), new Map(), new Map(), new Map()];
    this.ownDiscards.clear();
    this.pendingOwnDiscard = null;
    this.lastDiscardCount = 0;
  }

  private note(card: number, where: string, how: string): void {
    this.log.push({ hand: this.handNumber, card, where, how });
  }

  /** A card is now face up in the middle: nobody holds it. */
  private located(card: number): void {
    for (const m of this.held) m.delete(card);
    this.ownDiscards.delete(card);
  }

  observe(e: PublicEvent): void {
    if (e.handNumber !== this.handNumber) this.newHand(e.handNumber);
    const s = e.seat, t = e.action.type;
    for (const c of e.middleAfter) if (c) this.located(cardKey(c));
    if (t === "SWAP_ONE") {
      // the one middle position that changed: before = what they took, after = what they gave
      for (let i = 0; i < 5; i++) {
        const b = e.middleBefore[i], a = e.middleAfter[i];
        if (b && a && cardKey(b) !== cardKey(a)) {
          if (s !== this.me) { this.held[s].set(cardKey(b), { p: 1, reshuffledSince: false }); this.note(cardKey(b), `seat${s}`, "took from the middle"); }
          this.note(cardKey(a), "middle", `seat${s} put down`);
        }
      }
    } else if (t === "TAKE_MIDDLE") {
      if (s !== this.me) {
        this.held[s].clear();
        // a face-up middle is now entirely theirs; a face-down one is unknown to us
        for (const c of e.middleBefore) if (c) { this.held[s].set(cardKey(c), { p: 1, reshuffledSince: false }); this.note(cardKey(c), `seat${s}`, "took the whole middle"); }
      }
      for (const c of e.middleAfter) if (c) this.note(cardKey(c), "middle", `seat${s} dumped their hand`);
    } else if (t === "BLIND") {
      const reshuffled = e.discardCount !== this.lastDiscardCount + 1;
      if (reshuffled) {
        // the old discards became the new bones: back in circulation, position unknown
        for (const c of this.ownDiscards) this.note(c, "bones", "discards shuffled into new bones");
        this.ownDiscards.clear();
        for (const m of this.held) for (const h of m.values()) h.reshuffledSince = true;
      }
      if (s === this.me) {
        if (this.pendingOwnDiscard) { const k = cardKey(this.pendingOwnDiscard); this.ownDiscards.add(k); this.note(k, "discard", "I discarded it"); }
        this.pendingOwnDiscard = null;
      } else {
        // they threw away one of five unseen-to-us positions: each tracked card may have gone
        for (const [k, h] of this.held[s]) { h.p *= 4 / 5; h.reshuffledSince = false; this.note(k, `seat${s}?`, `p=${h.p.toFixed(2)} after a blind draw`); }
      }
    }
    this.lastDiscardCount = e.discardCount;
  }

  onHandEnd(r: HandRecord): void {
    for (let s = 0; s < 5; s++) this.handsSeen[s]++;
    this.wins[r.winnerSeat]++;
    if (r.knockerSeat >= 0) {
      this.knocks[r.knockerSeat]++;
      const res = r.results.find((x) => x.seat === r.knockerSeat);
      if (res) this.knockCategorySum[r.knockerSeat] += res.rank.category;
    }
  }

  /** Each player's chance of winning a hand, smoothed towards 1 in 5. */
  winRates(): number[] {
    return this.wins.map((w, s) => (w + 2) / (this.handsSeen[s] + 10));
  }

  /**
   * How readily a player knocks, 0..1 (0.5 = like the baseline AI). Beta(2, 8) prior on the knock
   * rate and a prior knocking hand of Two Pair–Trips, each worth ~10 hands of evidence.
   */
  eagerness(seat: number): number {
    const rate = (this.knocks[seat] + 2) / (this.handsSeen[seat] + 10);           // prior 0.2
    const strength = (this.knockCategorySum[seat] + 2.5 * 4) / (this.knocks[seat] + 4); // prior 2.5
    const e = 0.5 + (rate - 0.2) * 1.5 - (strength - 2.5) * 0.15;
    return Math.max(0, Math.min(1, e));
  }
}
